/**
 * useUploader — uploads captured frames to Azure Blob via SAS URLs.
 *
 * Handles: batch SAS URL fetching, parallel uploads (4 concurrent),
 * progress tracking, retry logic, and resume after interruption.
 *
 * Ends when the frames are in Azure. It does NOT queue or wait for a reconstruction: building
 * the 3D scene is tools/auto_3d.py's job, on a machine with a GPU, minutes or hours later.
 *
 * Frames are persisted in IndexedDB by useCamera. This hook tracks upload progress
 * there so interrupted uploads can be resumed after browser kill / phone call.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { createScan, getUploadUrls, uploadFrame, markUploadComplete } from '@services/scanApi'
import {
  getFramesBySession,
  getPendingFrames,
  updateSession,
  deleteSessionAndFrames,
} from '@services/frameStore'
import type { ScanSession, PersistedFrame } from '@services/frameStore'
import { apiUrl } from '../config'
import { getAuthHeaders } from '../stores/authStore'
import type { Scan, UploadUrl } from '@app-types/scan'

interface FramePair {
  blob: Blob     // Low-res for COLMAP/navigation
  hdBlob: Blob   // HD for zoom display
  ultraBlob?: Blob // 4K for deep zoom (only when ultra tier)
}

interface UploadProgress {
  total: number
  uploaded: number
  failed: number
  percent: number
}

type UploadPhase = 'idle' | 'creating' | 'uploading' | 'done' | 'error'

const CONCURRENT_UPLOADS = 4
const MAX_RETRIES = 5

/**
 * A blob-path-safe section id derived from the section's name.
 *
 * The slug is ASCII because it becomes a folder name in Azure, and stripping to [a-z0-9] leaves
 * NOTHING at all for a name written in Arabic -- which is most of them. That produced sections
 * uploaded to `{shop}/sec_/v1/`: a real folder of real frames under an id no shop document ever
 * refers to, invisible to every pipeline that looks a section up by id. The random suffix is the
 * guard: an id that cannot be derived is still unique, still matches the document, and is still
 * found later.
 */
export function deriveSectionId(sectionName: string): string {
  const slug = sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
  return 'sec_' + (slug || Math.random().toString(16).slice(2, 10))
}

export function useUploader() {
  const [phase, setPhase] = useState<UploadPhase>('idle')
  const [progress, setProgress] = useState<UploadProgress>({ total: 0, uploaded: 0, failed: 0, percent: 0 })
  const [scan, setScan] = useState<Scan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isUploadingRef = useRef(false)
  const abortedRef = useRef(false)

  // Warn user before closing browser during upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploadingRef.current) {
        e.preventDefault()
        e.returnValue = 'Upload in progress. Are you sure you want to leave?'
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // Auto-restart stalled uploads when page becomes visible again (e.g. after phone call)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isUploadingRef.current && abortedRef.current) {
        console.log('[useUploader] Page became visible, upload was stalled — will retry on next batch')
        abortedRef.current = false
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  /**
   * Give the shop the scan's location, if it does not have one yet.
   *
   * This used to hang off the end of the processing poll, and so only ran once COLMAP had
   * finished. It has nothing to do with the reconstruction -- the coordinates come from the
   * phone during capture -- so it belongs with the upload, which is the last thing this hook
   * still waits for. Best-effort throughout: a shop that keeps its old location is a far smaller
   * problem than a scan that reports failure because a PATCH did not land.
   */
  const adoptShopLocation = useCallback(
    async (shopId: string, location?: { lat: number; lng: number } | null) => {
      if (!location) return
      try {
        const shopRes = await fetch(apiUrl(`/api/shops/${shopId}`), { headers: getAuthHeaders() })
        if (!shopRes.ok) return
        const shop = await shopRes.json()
        if (shop.location != null) return
        await fetch(apiUrl(`/api/shops/${shopId}`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ location }),
        })
        console.log('✓ [useUploader] Updated shop location from scan:', location)
      } catch (err) {
        console.warn('⚠️ [useUploader] Failed to update shop location:', err)
      }
    },
    [],
  )

  /**
   * Core upload logic: uploads frame blobs for a specific tier using SAS URLs.
   * tier = 'lowres' | 'hd' | 'ultra'
   */
  const uploadFrameBatch = useCallback(
    async (
      frames: PersistedFrame[],
      urls: UploadUrl[],
      tier: 'lowres' | 'hd' | 'ultra',
      _scanSessionId: string,
      startUploaded: number,
    ): Promise<{ uploaded: number; failed: number }> => {
      let uploaded = startUploaded
      let failed = 0
      const total = startUploaded + frames.length

      setProgress({ total, uploaded, failed: 0, percent: Math.round((uploaded / total) * 100) })

      // Build a map from frameIndex to URL info
      const urlByIndex = new Map<number, UploadUrl>()
      for (const u of urls) urlByIndex.set(u.frame_index, u)

      // Use lower concurrency for background HD/ultra uploads
      const concurrency = tier === 'lowres' ? CONCURRENT_UPLOADS : 2

      // Process in batches
      for (let i = 0; i < frames.length; i += concurrency) {
        const batch = frames.slice(i, i + concurrency)
        const promises = batch.map(async (frame) => {
          const urlInfo = urlByIndex.get(frame.frameIndex)
          if (!urlInfo) {
            console.error(`[useUploader] No SAS URL for frame index ${frame.frameIndex}`)
            failed++
            return
          }

          let uploadUrl: string | undefined
          let blob: Blob | undefined

          if (tier === 'lowres') {
            uploadUrl = urlInfo.upload_url
            blob = frame.blob
          } else if (tier === 'hd') {
            uploadUrl = urlInfo.hd_upload_url
            blob = frame.hdBlob
          } else if (tier === 'ultra') {
            uploadUrl = urlInfo.ultra_upload_url
            blob = frame.ultraBlob
          }

          if (!uploadUrl || !blob) {
            // No URL or blob for this tier — skip silently
            uploaded++
            setProgress({ total, uploaded, failed, percent: Math.round(((uploaded + failed) / total) * 100) })
            return
          }

          try {
            await uploadFrame(uploadUrl, blob, MAX_RETRIES)
            uploaded++
          } catch {
            failed++
            console.error(`Failed to upload ${tier} frame ${frame.frameIndex}`)
          }
          setProgress({ total, uploaded, failed, percent: Math.round(((uploaded + failed) / total) * 100) })
        })
        await Promise.all(promises)
      }

      return { uploaded, failed }
    },
    [],
  )

  // ── Fresh upload (from capture) ──
  const upload = useCallback(
    async (
      framePairs: FramePair[],
      shopId: string,
      sectionName: string,
      sectionId?: string,
      location?: { lat: number; lng: number } | null,
      scanSessionId?: string,
      rescanOptions?: { mode: 'rescan'; anchor_frame_index?: number },
      includeUltra?: boolean,
      sectionCategory?: string,
    ) => {
      try {
        setError(null)
        setPhase('creating')
        isUploadingRef.current = true
        abortedRef.current = false

        // Use provided sectionId or generate from name
        const finalSectionId = sectionId || deriveSectionId(sectionName)

        // Step 1: Create scan (with location if available)
        const newScan = await createScan({
          shop_id: shopId,
          section_id: finalSectionId,
          section_name: sectionName,
          frame_count: framePairs.length,
          location: location || null,
          ...(sectionCategory ? { section_category: sectionCategory } : {}),
          ...(rescanOptions ? { mode: rescanOptions.mode, anchor_frame_index: rescanOptions.anchor_frame_index } : {}),
        })
        setScan(newScan)

        // Update IndexedDB session with server scan ID
        if (scanSessionId) {
          await updateSession(scanSessionId, { serverScanId: newScan.id, status: 'uploading' })
        }

        // Step 2: Get SAS upload URLs (includes standard + HD + optional ultra URLs)
        setPhase('uploading')
        const { urls, marker_upload_url: markerUrl } = await getUploadUrls(newScan.id, framePairs.length, includeUltra)

        // Step 3: Get frames from IndexedDB if session is available, otherwise use in-memory blobs
        let framesToUpload: PersistedFrame[]
        if (scanSessionId) {
          framesToUpload = await getFramesBySession(scanSessionId)
          // Sort by frameIndex to match URL order
          framesToUpload.sort((a, b) => a.frameIndex - b.frameIndex)
        } else {
          // Fallback: create temporary PersistedFrame objects from in-memory framePairs
          framesToUpload = framePairs.map((fp, idx) => ({
            id: `temp_${idx + 1}`,
            scanSessionId: 'temp',
            frameIndex: idx + 1,
            blob: fp.blob,
            hdBlob: fp.hdBlob,
            ...(fp.ultraBlob ? { ultraBlob: fp.ultraBlob } : {}),
            status: 'pending' as const,
            createdAt: Date.now(),
          }))
        }

        // ── Phase 1: Upload low-res frames (priority — COLMAP needs these) ──
        const lowRes = await uploadFrameBatch(framesToUpload, urls, 'lowres', scanSessionId || 'temp', 0)

        if (lowRes.failed > 0) {
          console.warn(`[useUploader] ${lowRes.failed} low-res frame uploads failed`)
        }

        // Step 4: The frames are in Azure — that is the whole job.
        //
        // This used to mark the scan complete (queueing the old COLMAP worker) and then poll it
        // for minutes while the person who did the scanning watched a spinner. Nothing about the
        // reconstruction needs them present for it: tools/auto_3d.py sweeps for sections without
        // a scene and builds them on the GPU machine. So the capture ends where the capture
        // actually ends, and the 3D arrives later on its own.
        // The marker goes last, and only if nothing failed: it is a promise to the automation
        // that every frame is there, and a scan missing frames must not make that promise. A
        // section without it is skipped and retried on a later sweep, which is the safe way to
        // be wrong.
        if (markerUrl && lowRes.failed === 0) {
          await markUploadComplete(markerUrl, framesToUpload.length).catch((err) => {
            console.warn('[useUploader] Could not write upload marker:', err)
          })
        }

        if (scanSessionId) {
          await updateSession(scanSessionId, { status: 'completed' })
        }
        adoptShopLocation(shopId, location)
        setPhase('done')

        // ── Phase 2: Background upload HD frames (non-blocking) ──
        console.log('[useUploader] Starting background HD upload...')
        uploadFrameBatch(framesToUpload, urls, 'hd', scanSessionId || 'temp', 0)
          .then(({ failed }) => {
            if (failed > 0) console.warn(`[useUploader] ${failed} HD uploads failed`)
            else console.log('[useUploader] HD upload complete')

            // ── Phase 3: Background upload ultra frames (if applicable) ──
            if (includeUltra) {
              console.log('[useUploader] Starting background 4K upload...')
              return uploadFrameBatch(framesToUpload, urls, 'ultra', scanSessionId || 'temp', 0)
            }
          })
          .then((result) => {
            if (result && result.failed > 0) console.warn(`[useUploader] ${result.failed} ultra uploads failed`)
            else if (result) console.log('[useUploader] 4K upload complete')

            isUploadingRef.current = false

            // Clean up IndexedDB after all tiers uploaded
            if (scanSessionId) {
              deleteSessionAndFrames(scanSessionId).catch(() => {})
            }
          })
          .catch((err) => {
            console.warn('[useUploader] Background upload error:', err)
            isUploadingRef.current = false
          })
      } catch (err) {
        isUploadingRef.current = false
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setError(msg)
        setPhase('error')

        // Mark session as failed but keep frames for retry
        if (scanSessionId) {
          await updateSession(scanSessionId, { status: 'ready' }).catch(() => {})
        }
      }
    },
    [uploadFrameBatch, adoptShopLocation],
  )

  // ── Resume interrupted upload ──
  const resume = useCallback(
    async (session: ScanSession) => {
      try {
        setError(null)
        isUploadingRef.current = true
        abortedRef.current = false

        // Get remaining pending frames from IndexedDB
        const pendingFrames = await getPendingFrames(session.id)
        if (pendingFrames.length === 0) {
          setError('No pending frames found to resume')
          setPhase('error')
          isUploadingRef.current = false
          return
        }

        pendingFrames.sort((a, b) => a.frameIndex - b.frameIndex)
        const alreadyUploaded = session.frameCount - pendingFrames.length

        // Check if any frames have ultraBlob (indicates ultra tier scan)
        const hasUltra = pendingFrames.some((f) => f.ultraBlob != null)

        // If we don't have a server scan ID yet, create one
        let serverScanId = session.serverScanId
        if (!serverScanId) {
          setPhase('creating')
          const newScan = await createScan({
            shop_id: session.shopId,
            section_id: session.sectionId,
            section_name: session.sectionName,
            frame_count: session.frameCount,
            location: session.location || null,
          })
          setScan(newScan)
          serverScanId = newScan.id
          await updateSession(session.id, { serverScanId, status: 'uploading' })
        }

        // Request SAS URLs for ALL frames (backend generates for frame_count starting at 1)
        setPhase('uploading')
        const { urls, marker_upload_url: markerUrl } = await getUploadUrls(serverScanId, session.frameCount, hasUltra)
        await updateSession(session.id, { status: 'uploading' })

        // ── Phase 1: Upload low-res frames (priority) ──
        const lowRes = await uploadFrameBatch(pendingFrames, urls, 'lowres', session.id, alreadyUploaded)

        if (lowRes.failed > 0) {
          console.warn(`[useUploader] Resume: ${lowRes.failed} low-res uploads failed`)
        }

        // Frames are in Azure -- done, exactly as in upload() above. No queue, no polling:
        // tools/auto_3d.py builds the scene later without anyone waiting on it.
        if (markerUrl && lowRes.failed === 0) {
          await markUploadComplete(markerUrl, session.frameCount).catch((err) => {
            console.warn('[useUploader] Could not write upload marker:', err)
          })
        }
        await updateSession(session.id, { status: 'completed' })
        adoptShopLocation(session.shopId, session.location)
        setPhase('done')

        // ── Phase 2+3: Background HD + ultra upload ──
        uploadFrameBatch(pendingFrames, urls, 'hd', session.id, 0)
          .then(({ failed }) => {
            if (failed > 0) console.warn(`[useUploader] ${failed} HD uploads failed`)
            if (hasUltra) {
              return uploadFrameBatch(pendingFrames, urls, 'ultra', session.id, 0)
            }
          })
          .then((result) => {
            if (result && result.failed > 0) console.warn(`[useUploader] ${result.failed} ultra uploads failed`)
            isUploadingRef.current = false
            deleteSessionAndFrames(session.id).catch(() => {})
          })
          .catch((err) => {
            console.warn('[useUploader] Background upload error:', err)
            isUploadingRef.current = false
          })
      } catch (err) {
        isUploadingRef.current = false
        const msg = err instanceof Error ? err.message : 'Resume upload failed'
        setError(msg)
        setPhase('error')

        // Keep frames for another retry
        await updateSession(session.id, { status: 'ready' }).catch(() => {})
      }
    },
    [uploadFrameBatch, adoptShopLocation],
  )

  // Reset state
  const reset = useCallback(() => {
    isUploadingRef.current = false
    abortedRef.current = false
    setPhase('idle')
    setProgress({ total: 0, uploaded: 0, failed: 0, percent: 0 })
    setScan(null)
    setError(null)
  }, [])

  return { phase, progress, scan, error, upload, resume, reset }
}
